import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { CardStyle, DocActionButton, PositionIcon, Size } from '../../../base/src/abcCard/abcCard';

enum TemplateEnum {
  Large,
  Medium,
  Small,
}

/**
 * Represents a single metadata item displayed in the card's subtitle section.
 */
export interface SecondaryMetaItem {
  /** The path to the icon image or the name of the material icon. */
  icon: string;
  /** The text content to display next to the icon. */
  text: string;
  /** Whether the icon is an image (true) or a material icon (false). */
  img: boolean;
  /** The tooltip text to show on hover. */
  tooltip?: string;
  /** Optional CSS class for the metadata item. */
  class?: string;
}

@Component({
  selector: '',
  template: `
    <mat-card
      [dir]="rtlEnabled"
      [class]="class"
      #matCard
      [id]="id"
      (click)="this.onCardClick.emit()"
      (dblclick)="this.onCardDblClick.emit()"
      [class.subDivContentCardList]="colorBar"
      [attr.style]="'border-left-color:' + colorBar + '!important; height:' + height + 'px !important'"
    >
      <mat-card-header class="matCardHeader" fxLayout="row" fxFlex>
        <div fxLayout="column" fxLayoutAlign="start" fxFlex fxLayoutGap="0px" class="header">
          @if (displayText) {
            <mat-card-title
              class="ellipsisOneLine"
              [id]="cardId"
              (mouseenter)="checkTruncation(cardId, 'title')"
              [matTooltipDisabled]="disabledTitleTooltip || !isTitleTruncated"
              [matTooltip]="displayText"
              matTooltipClass="multiline-tooltip"
              [matTooltipShowDelay]="tooltipDelay"
              [ngClass]="[!secondaryDisplayText ? 'centredTitle' : '', isTitleTruncated ? 'truncated-text' : '']"
            >
              @if (leftIconPosition === positionIcon.Top) {
                <mat-icon fxFlex="32px" class="abcCardMatIcon" svgIcon="{{ leftIconPath }}"></mat-icon>
              }
              <span [innerHTML]="displayText | highlightSearch: searchInput"></span>
            </mat-card-title>
          }
          @if (secondaryBase) {
            <mat-card-subtitle
              class="ellipsisOneLine"
              [id]="subtitleId"
              (mouseenter)="checkTruncation(subtitleId, 'subtitle')"
              [matTooltipDisabled]="disabledSubTitleTooltip || !isSubtitleTruncated"
              [matTooltip]="secondaryBase"
              matTooltipClass="multiline-tooltip"
              [matTooltipShowDelay]="tooltipDelay"
              [ngClass]="isSubtitleTruncated ? 'truncated-text' : ''"
            >
              <span [innerHTML]="secondaryBase"></span>
            </mat-card-subtitle>
          }
          @if (headerDescription) {
            <div
              fxLayout="row"
              class="description ellipsisOneLine"
              [id]="descriptionId"
              (mouseenter)="checkTruncation(descriptionId, 'description')"
              matTooltipClass="multiline-tooltip"
              [matTooltipDisabled]="disabledDescriptionTooltip || !isDescriptionTruncated"
              [matTooltip]="description"
              [matTooltipShowDelay]="tooltipDelay"
              [ngClass]="isDescriptionTruncated ? 'truncated-text' : ''"
            >
              <span>{{ description }}</span>
            </div>
          }
          @if (secondaryMeta?.length) {
            <div class="subtitle-lines" fxLayout="column" fxLayoutGap="4px">
              @for (meta of secondaryMeta; track meta; let i = $index) {
                <div fxLayout="row" fxLayoutAlign="start center" class="subtitle-line" [ngClass]="meta.class">
                  @if (!meta.img) {
                    <mat-icon
                      [matTooltipDisabled]="disabledSecondarySubTitleTooltip"
                      [matTooltip]="meta.tooltip || meta.text"
                      matTooltipClass="multiline-tooltip"
                      [matTooltipShowDelay]="tooltipDelay"
                      >{{ meta.icon }}</mat-icon
                    >
                  } @else {
                    <img
                      [src]="meta.icon"
                      alt=""
                      [matTooltipDisabled]="disabledSecondarySubTitleTooltip"
                      [matTooltip]="meta.tooltip || meta.text"
                      matTooltipClass="multiline-tooltip"
                      [matTooltipShowDelay]="tooltipDelay"
                    />
                  }
                  <span
                    class="ellipsisOneLine meta-text"
                    [id]="'meta-text-' + i"
                    [matTooltipDisabled]="!isMetaTruncated('meta-text-' + i)"
                    [matTooltip]="meta.text"
                    matTooltipClass="multiline-tooltip"
                    [matTooltipShowDelay]="tooltipDelay"
                    (mouseenter)="checkMetaTruncation('meta-text-' + i)"
                    [ngClass]="isMetaTruncated('meta-text-' + i) ? 'truncated-text' : ''"
                    [innerHTML]="meta.text"
                  ></span>
                </div>
              }
            </div>
          }
        </div>
        @if (leftIconPosition === positionIcon.Middle) {
          <div
            [ngClass]="{ sizeIconLarge: leftIconSize === size.Large, sizeIconSmall: leftIconSize === size.Small }"
            [ngStyle]="{ overflow: leftIsImg ? 'hidden' : 'visible' }"
            mat-card-avatar
            class="cardAvatar"
          >
            @if (leftIsImg) {
              <div>
                @if (leftIconPath) {
                  <img [src]="leftIconPath" class="rightImg" />
                }
                @if (!leftIconPath) {
                  <mat-icon svgIcon="profile-user" class="defaultIcon"></mat-icon>
                }
              </div>
            }
            @if (!leftIsImg) {
              <mat-icon fxFlex class="divIconCard" svgIcon="{{ leftIconPath }}"> </mat-icon>
            }
          </div>
        }

        @if (!rightIsImg && rightIconPath) {
          <button
            mat-flat-button
            class="matButtonContact"
            [matTooltipDisabled]="disabledRightImgTooltip"
            [matTooltip]="rightIconTooltip"
            matTooltipClass="multiline-tooltip"
            [matTooltipShowDelay]="tooltipDelay"
          >
            <mat-icon style="background-size: 10px 10px;" class="arrowIcon" svgIcon="{{ rightIconPath }}"></mat-icon>
          </button>
        }
        @if (rightIsImg) {
          <div
            class="rightIsImg"
            [matTooltipDisabled]="disabledRightImgTooltip"
            [matTooltip]="rightIconTooltip"
            matTooltipClass="multiline-tooltip"
            [matTooltipShowDelay]="tooltipDelay"
          >
            @if (rightIconPath) {
              <img [src]="rightIconPath" class="rightImg" />
            }
            @if (!rightIconPath && !shortHand) {
              <mat-icon svgIcon="profile-user" class="defaultIcon"></mat-icon>
            }
            @if (!rightIconPath && shortHand) {
              <div class="cardFirstLetters">
                {{ shortHand }}
              </div>
            }
          </div>
        }
      </mat-card-header>
      @if (description && !headerDescription) {
        <mat-card-content> {{ description }} </mat-card-content>
      }
      @if (primaryActionName || secondaryActionName || buttons) {
        <mat-card-actions>
          @if (buttons) {
            <div
              class="cardButtonsStyle"
              [matTooltip]="buttonsTooltip"
              matTooltipClass="multiline-tooltip"
              [matTooltipShowDelay]="tooltipDelay"
              [matTooltipDisabled]="!buttonsTooltip"
            >
              @for (btn of buttons; track btn) {
                <button
                  mat-mdc-icon-button
                  class="card-action-button"
                  [disabled]="btn.disabled || !btn.visible"
                  [matTooltipDisabled]="disabledButtonTooltip"
                  [matTooltip]="btn.tooltip"
                  matTooltipClass="multiline-tooltip"
                  [matTooltipShowDelay]="tooltipDelay"
                  (click)="btn.click(); $event.stopPropagation()"
                >
                  @if (btn.visible) {
                    <mat-icon [svgIcon]="btn.icon" [id]="btn.id"></mat-icon>
                  }
                  @if (btn.label) {
                    <span class="card-action-label">{{ btn.label }}</span>
                  }
                </button>
              }
            </div>
          }
          @if (primaryActionName) {
            <button
              mat-mdc-icon-button
              [matTooltipDisabled]="disabledButtonTooltip"
              [matTooltip]="primaryActionTooltip || primaryActionName"
              matTooltipClass="multiline-tooltip"
              [matTooltipShowDelay]="tooltipDelay"
            >
              <mat-icon
                class="customIconSize"
                [svgIcon]="primaryActionName"
                (click)="primaryActionClick(); $event.stopPropagation()"
              >
              </mat-icon>
            </button>
          }
          @if (secondaryActionName) {
            <button
              mat-mdc-icon-button
              [matTooltipDisabled]="disabledButtonTooltip"
              [matTooltip]="secondaryActionTooltip || secondaryActionName"
              matTooltipClass="multiline-tooltip"
              [matTooltipShowDelay]="tooltipDelay"
            >
              <mat-icon class="customIconSize" [svgIcon]="secondaryActionName" (click)="secondaryActionClick()">
              </mat-icon>
            </button>
          }
          @if (thirdActionName) {
            <button
              mat-mdc-icon-button
              [matTooltipDisabled]="disabledButtonTooltip"
              [matTooltip]="thirdActionTooltip || thirdActionName"
              matTooltipClass="multiline-tooltip"
              [matTooltipShowDelay]="tooltipDelay"
            >
              <mat-icon class="customIconSize" [svgIcon]="thirdActionName" (click)="thirdActionClick()"> </mat-icon>
            </button>
          }
          @if (fourthActionName) {
            <button
              mat-mdc-icon-button
              [matTooltipDisabled]="disabledButtonTooltip"
              [matTooltip]="fourthActionTooltip || fourthActionName"
              matTooltipClass="multiline-tooltip"
              [matTooltipShowDelay]="tooltipDelay"
            >
              <mat-icon class="customIconSize" [svgIcon]="fourthActionName" (click)="fourthActionClick()"> </mat-icon>
            </button>
          }
          <!-- <button mat-mdc-icon-button>
          <mat-icon class="customIconSize" [svgIcon]="'moreOptions'"> </mat-icon>
        </button> -->
        </mat-card-actions>
      }
    </mat-card>
  `,
  styleUrls: ['./matCard.component.scss'],
  standalone: false,
})
export class MatCardComponent implements AfterViewInit, AfterViewChecked, OnChanges {
  @Input() id: any;
  private _displayText: string;
  @Input()
  get displayText() {
    return this._displayText;
  }
  set displayText(value: string) {
    this._displayText = value;
    this.checkTruncation(this.cardId, 'title');
    this.cdr.detectChanges();
  }
  @Input() icon: string;
  @Input() buttons: DocActionButton[];
  @Input() buttonsTooltip: string;
  private _secondaryDisplayText: string;
  @Input()
  get secondaryDisplayText() {
    return this._secondaryDisplayText;
  }
  set secondaryDisplayText(value: string) {
    this._secondaryDisplayText = value;
    this.computeSecondaryParts();
    this.checkTruncation(this.subtitleId, 'subtitle');
    this.secondaryMeta.forEach((_, i) => {
      this.checkMetaTruncation('meta-text-' + i);
    });
    this.cdr.detectChanges();
  }
  @Input() template: number;
  @Input() searchInput: string;
  @Input() height: string | number;
  @Input() width: string;
  @Input() image: string;
  @Input() colorBar: string;
  @Input() leftIconPath: string;
  @Input() rightIconSize: number;
  @Input() leftIconSize: number;
  @Input() leftIconPosition: number;
  @Input() rightIconPath: string;
  @Input() rightIconTooltip: string;
  @Input() rightIsImg: boolean;
  @Input() leftIsImg: boolean;
  @Input() tooltipDelay: number = 0;
  @Input() disabledTooltip: boolean = false;
  @Input() disabledTitleTooltip: boolean = false;
  @Input() disabledSubTitleTooltip: boolean = false;
  @Input() disabledDescriptionTooltip: boolean = false;
  @Input() disabledRightImgTooltip: boolean = false;
  @Input() disabledButtonTooltip: boolean = false;
  @Input() disabledSecondarySubTitleTooltip: boolean = false;
  private _description: string;
  @Input()
  get description() {
    return this._description;
  }
  set description(value: string) {
    this._description = value;
    this.checkTruncation(this.descriptionId, 'description');
    this.cdr.detectChanges();
  }
  @Input() shortHand: string;
  @Input() primaryActionName;
  @Input() primaryActionTooltip: string;
  @Input() secondaryActionName;
  @Input() secondaryActionTooltip: string;
  @Input() thirdActionName;
  @Input() thirdActionTooltip: string;
  @Input() fourthActionName;
  @Input() fourthActionTooltip: string;
  @Input() rtlEnabled: string;
  @Input() headerDescription: boolean;
  @Input() class: string;
  @ViewChild('matCard', { static: true, read: ElementRef }) matCard;
  @Output() onCardClick = new EventEmitter();
  @Output() onCardDblClick = new EventEmitter();
  @Output() onPrimaryActionClick = new EventEmitter();
  @Output() onSecondaryActionClick = new EventEmitter();
  @Output() onThirdActionClick = new EventEmitter();
  @Output() onFourthActionClick = new EventEmitter();
  public templateLarge: TemplateEnum = TemplateEnum.Large;
  public templateSmall: TemplateEnum = TemplateEnum.Small;
  public size = Size;
  public positionIcon = PositionIcon;
  public cardId: string = 'card-title-' + Math.random().toString(36).substr(2, 9);
  public subtitleId: string = 'card-subtitle-' + Math.random().toString(36).substr(2, 9);
  public descriptionId: string = 'card-description-' + Math.random().toString(36).substr(2, 9);
  public errorImg: boolean = true;
  public secondaryBase: string = '';
  public secondaryMeta: SecondaryMetaItem[] = [];
  public isTitleTruncated: boolean = false;
  public isSubtitleTruncated: boolean = false;
  public isDescriptionTruncated: boolean = false;
  private metaTruncationState: Map<string, boolean> = new Map();
  private previousIsTitleTruncated: boolean = false;
  private previousIsSubtitleTruncated: boolean = false;
  private previousIsDescriptionTruncated: boolean = false;
  private previousMetaTruncationState: Map<string, boolean> = new Map();

  /**
   * Parses the secondaryDisplayText string into a base subtitle and a list of metadata items.
   * The input string is expected to be delimited by '||'.
   * Metadata items can have a prefix (e.g., 'img:', 'server:', 'db:') which determines the icon and tooltip.
   * This process identifies and maps ("sinification") raw text or prefixed values into structured objects.
   */
  private computeSecondaryParts(): void {
    const raw = (this._secondaryDisplayText || '').trim();
    if (!raw) {
      this.secondaryBase = '';
      this.secondaryMeta = [];
      return;
    }

    const parts = raw.split('||').map((s) => s.trim());
    const basePart = parts.find((p) => !p.startsWith('img:'));
    const metaParts = parts.filter((p) => p.startsWith('img:'));

    this.secondaryBase = basePart || '';
    const rest = metaParts.filter(Boolean);

    const meta = rest
      .map((item) => {
        const m = item.match(/^([a-zA-Z]+)\s*[:=]\s*(.*)$/);
        const key = (m?.[1] || '').toLowerCase();
        const value = (m?.[2] || item).trim();
        if (!value) return null;

        if (key === 'img') {
          const [src = '', text = '', ...extras] = value.split('|').map((s) => s.trim());
          if (!src || !text) return null;
          const tt = extras.find((s) => s.toLowerCase().startsWith('tt:'));
          const tooltip = tt ? tt.slice(3).trim() || text : text;
          return { icon: src, text, img: true, tooltip, class: 'secondaryMetaItem' };
        }

        return null;
      })
      .filter((x) => x && x.text);

    this.secondaryMeta = meta;
  }

  constructor(
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef,
  ) {
    this.headerDescription = true;
  }

  ngOnInit() {
    this.leftIconPosition = Number(this.leftIconPosition);
    this.leftIconSize = Number(this.leftIconSize);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['displayText'] || changes['secondaryDisplayText'] || changes['description']) {
      this.checkTruncation(this.cardId, 'title');
      this.checkTruncation(this.subtitleId, 'subtitle');
      this.checkTruncation(this.descriptionId, 'description');
      this.secondaryMeta.forEach((_, i) => {
        this.checkMetaTruncation('meta-text-' + i);
      });
      this.cdr.detectChanges();
    }
  }

  ngAfterViewInit(): void {
    this.cdr.detectChanges();
    this.checkTruncation(this.cardId, 'title');
    this.checkTruncation(this.subtitleId, 'subtitle');
    this.checkTruncation(this.descriptionId, 'description');
    this.secondaryMeta.forEach((_, i) => {
      this.checkMetaTruncation('meta-text-' + i);
    });
    this.cdr.detectChanges();
  }

  ngAfterViewChecked(): void {
    this.checkTruncation(this.cardId, 'title');
    this.checkTruncation(this.subtitleId, 'subtitle');
    this.checkTruncation(this.descriptionId, 'description');
    this.secondaryMeta.forEach((_, i) => {
      this.checkMetaTruncation('meta-text-' + i);
    });
  }

  public checkTruncation(id: string, type: 'title' | 'subtitle' | 'description'): void {
    const e = document.getElementById(id);
    if (!e) return;
    let isTruncated: boolean;

    if (type === 'description') {
      const textElement = e.querySelector('span') || e;
      isTruncated = textElement.scrollHeight > textElement.clientHeight;
    } else {
      isTruncated = e.scrollWidth > e.clientWidth;
    }

    if (type === 'title' && isTruncated !== this.previousIsTitleTruncated) {
      this.isTitleTruncated = isTruncated;
      this.previousIsTitleTruncated = isTruncated;
      this.cdr.detectChanges();
    } else if (type === 'subtitle' && isTruncated !== this.previousIsSubtitleTruncated) {
      this.isSubtitleTruncated = isTruncated;
      this.previousIsSubtitleTruncated = isTruncated;
      this.cdr.detectChanges();
    } else if (type === 'description' && isTruncated !== this.previousIsDescriptionTruncated) {
      this.isDescriptionTruncated = isTruncated;
      this.previousIsDescriptionTruncated = isTruncated;
      this.cdr.detectChanges();
    }
  }

  public checkMetaTruncation(id: string): void {
    const e = document.getElementById(id);
    if (!e) return;
    const isTruncated = e.scrollWidth > e.clientWidth;
    const previous = this.previousMetaTruncationState.get(id);
    if (isTruncated !== previous) {
      this.metaTruncationState.set(id, isTruncated);
      this.previousMetaTruncationState.set(id, isTruncated);
      this.cdr.detectChanges();
    }
  }

  public isMetaTruncated(id: string): boolean {
    return this.metaTruncationState.get(id) ?? false;
  }

  public isEllipsisActive(id): void {
    this.checkTruncation(id, 'title');
  }

  public setCardStyle(style: CardStyle): void {
    this.height = style.height;
    const element = this.matCard.nativeElement;
    if (style) {
      for (const key in style) {
        element.style[key] = style[key];
      }
    }
  }

  public primaryActionClick(): void {
    this.onPrimaryActionClick.emit();
  }

  public secondaryActionClick(): void {
    this.onSecondaryActionClick.emit();
  }
  public thirdActionClick(): void {
    this.onThirdActionClick.emit();
  }

  public fourthActionClick(): void {
    this.onFourthActionClick.emit();
  }
}
